import {
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { PASSWORD_RULES, getPasswordStrength } from '@/lib/passwordValidation'

type PasswordStrengthIndicatorProps = {
  password: string
}

const colorMap = {
  error: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
  success: 'success.main'
} as const

export const PasswordStrengthIndicator = ({
  password
}: PasswordStrengthIndicatorProps) => {
  if (!password) return null

  const strength = getPasswordStrength(password)

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">Password strength</Typography>
        <Typography variant="body2" sx={{ color: colorMap[strength.color] }}>
          {strength.label}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={strength.percent}
        color={strength.color}
        sx={{ height: 6, borderRadius: 3 }}
      />

      <List dense disablePadding sx={{ mt: 1 }}>
        {PASSWORD_RULES.map((rule) => {
          const met = rule.test(password)
          return (
            <ListItem
              key={rule.key}
              disableGutters
              disablePadding
              sx={{ py: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {met ? (
                  <CheckCircleIcon
                    sx={{ fontSize: 16, color: 'success.main' }}
                  />
                ) : (
                  <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={rule.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: met ? 'text.secondary' : 'text.primary'
                }}
              />
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}

export default PasswordStrengthIndicator
